import { useCallback, useEffect, useMemo, useState } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import { useAppStore } from '../stores/appStore';

interface DocReferenceEntry {
  path: string;
  /** The directory context used to resolve this entry's path (parent's directory) */
  contextDir: string;
  /** The resolved directory of this entry (for resolving links within this doc) */
  resolvedDir: string;
  content: string | null;
  loading: boolean;
  error: string | null;
}

interface DocReferencePopupProps {
  /** The initial file path to display */
  filePath: string;
  /** Current context for resolving relative paths */
  context: {
    sourceType: 'agent' | 'project' | 'game';
    agentId?: string | null;
    archived?: boolean;
    projectName?: string | null;
    currentPath?: string;
  };
  onClose: () => void;
}

/** Derive HTTP base URL from the gateway WebSocket URL (same origin). */
function getPluginBaseUrl(): string {
  const gwUrl = useAppStore.getState().gatewayUrl;
  try {
    const u = new URL(gwUrl);
    const proto = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${proto}//${u.host}`;
  } catch {
    return window.location.origin;
  }
}

function getAuthHeaders(): Record<string, string> {
  const token = useAppStore.getState().gatewayToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Normalize a path by resolving `.` and `..` segments.
 * e.g. "docs/research/../player-love-framework.md" → "docs/player-love-framework.md"
 */
function normalizePath(path: string): string {
  const parts = path.split('/');
  const resolved: string[] = [];
  for (const part of parts) {
    if (part === '.' || part === '') continue;
    if (part === '..') {
      resolved.pop();
    } else {
      resolved.push(part);
    }
  }
  return resolved.join('/');
}

/**
 * Get the directory portion of a file path.
 * e.g. "docs/research/report.md" → "docs/research"
 *      "MEMORY.md" → ""
 */
function getDirectory(filePath: string): string {
  const lastSlash = filePath.lastIndexOf('/');
  return lastSlash >= 0 ? filePath.slice(0, lastSlash) : '';
}

/**
 * Check if a path is relative (starts with ./ or ../).
 */
function isRelativePath(path: string): boolean {
  return path.startsWith('./') || path.startsWith('../');
}

/**
 * Resolve a file path reference to an API URL.
 *
 * Handles patterns like:
 * - `~/projects/<project>/.../<file>.md` → project file API
 * - `../foo.md` (relative) → resolved against current document's directory
 * - `docs/foo.md` (relative) → resolved against current project context
 * - `MEMORY.md` (bare file) → resolved against current context
 */
function resolveFilePathToApiUrl(
  rawPath: string,
  context: DocReferencePopupProps['context'],
): string | null {
  const base = getPluginBaseUrl();
  let filePath = rawPath.trim();

  // Handle ~/projects/<projectName>/... pattern
  const homeProjectMatch = filePath.match(/^~\/projects\/([^/]+)\/(.+)$/);
  if (homeProjectMatch) {
    const projectName = homeProjectMatch[1];
    const relPath = homeProjectMatch[2];
    const urlPath = normalizePath(relPath).split('/').map(encodeURIComponent).join('/');
    return `${base}/api/projects/${encodeURIComponent(projectName)}/files/${urlPath}`;
  }

  // Handle absolute-ish paths starting with ~/
  // e.g. ~/some/path/file.md — can't resolve without more info
  // Try treating as a project-relative path if we strip ~/
  if (filePath.startsWith('~/')) {
    filePath = filePath.slice(2);
  }

  // Resolve relative paths (../ or ./) against the current document's directory
  if (isRelativePath(filePath) && context.currentPath) {
    // currentPath is the directory of the document being viewed
    const combined = `${context.currentPath}/${filePath}`;
    filePath = normalizePath(combined);
  } else if (filePath.startsWith('./')) {
    // ./ with no currentPath — just strip the prefix
    filePath = filePath.slice(2);
  }

  // For project context: resolve relative to current path within the project
  if (context.sourceType === 'project' && context.projectName) {
    // If not a relative path (already resolved above), prefix with currentPath
    const resolvedPath = !isRelativePath(rawPath.trim()) && context.currentPath && !filePath.startsWith(context.currentPath)
      ? `${context.currentPath}/${filePath}`
      : filePath;
    const normalized = normalizePath(resolvedPath);
    const urlPath = normalized.split('/').map(encodeURIComponent).join('/');
    return `${base}/api/projects/${encodeURIComponent(context.projectName)}/files/${urlPath}`;
  }

  // For game context: resolve relative to game project
  if (context.sourceType === 'game') {
    const urlPath = normalizePath(filePath).split('/').map(encodeURIComponent).join('/');
    return `${base}/api/projects/game/files/${urlPath}`;
  }

  // For agent context: the file name is just the filename (agents have flat file lists)
  if (context.sourceType === 'agent' && context.agentId) {
    // Agent files are flat — just use the basename
    const fileName = filePath.split('/').pop() ?? filePath;
    const archivedParam = context.archived ? '&archived=true' : '';
    return `${base}/api/files/read?agentId=${encodeURIComponent(context.agentId)}&name=${encodeURIComponent(fileName)}${archivedParam}`;
  }

  return null;
}

/**
 * Resolve a raw file path to its full project-relative path (not a URL).
 * Used to compute the resolved directory for nested reference resolution.
 */
function resolveToFullPath(
  rawPath: string,
  context: DocReferencePopupProps['context'],
): string {
  let filePath = rawPath.trim();

  // ~/projects/<project>/path → extract path portion
  const homeProjectMatch = filePath.match(/^~\/projects\/[^/]+\/(.+)$/);
  if (homeProjectMatch) {
    return normalizePath(homeProjectMatch[1]);
  }

  if (filePath.startsWith('~/')) {
    filePath = filePath.slice(2);
  }

  if (isRelativePath(filePath) && context.currentPath) {
    const combined = `${context.currentPath}/${filePath}`;
    return normalizePath(combined);
  }

  if (filePath.startsWith('./')) {
    filePath = filePath.slice(2);
  }

  // For non-relative paths in project context, prefix with currentPath
  if ((context.sourceType === 'project' || context.sourceType === 'game') &&
      context.currentPath && !isRelativePath(rawPath.trim())) {
    return normalizePath(`${context.currentPath}/${filePath}`);
  }

  return normalizePath(filePath);
}

async function fetchFileContent(
  rawPath: string,
  context: DocReferencePopupProps['context'],
): Promise<string> {
  const apiUrl = resolveFilePathToApiUrl(rawPath, context);
  if (!apiUrl) {
    throw new Error(`Cannot resolve path: ${rawPath}`);
  }

  const resp = await fetch(apiUrl, {
    headers: getAuthHeaders(),
  });

  if (!resp.ok) {
    if (resp.status === 404) {
      throw new Error(`File not found: ${rawPath}`);
    }
    throw new Error(`Failed to load file (${resp.status})`);
  }

  const body = await resp.json() as { content?: string };
  return body.content ?? '';
}

export default function DocReferencePopup({ filePath, context, onClose }: DocReferencePopupProps) {
  // Compute the resolved directory for the initial file
  const initialContextDir = context.currentPath ?? '';
  const initialResolvedDir = useMemo(() => {
    const fullPath = resolveToFullPath(filePath, context);
    return getDirectory(fullPath);
  }, [filePath, context]);

  const [stack, setStack] = useState<DocReferenceEntry[]>([
    { path: filePath, contextDir: initialContextDir, resolvedDir: initialResolvedDir, content: null, loading: true, error: null },
  ]);

  const current = stack[stack.length - 1];

  // Build context for fetching — uses the entry's contextDir (parent's directory)
  // to correctly resolve the entry's own raw path
  const fetchContext = useMemo(() => {
    if (!current) return context;
    return {
      ...context,
      currentPath: current.contextDir,
    };
  }, [context, current?.contextDir]);

  // Fetch content for the current (top of stack) entry
  useEffect(() => {
    if (!current || !current.loading) return;

    let cancelled = false;
    void fetchFileContent(current.path, fetchContext).then(
      (content) => {
        if (cancelled) return;
        setStack((prev) =>
          prev.map((entry, i) =>
            i === prev.length - 1 ? { ...entry, content, loading: false } : entry,
          ),
        );
      },
      (err) => {
        if (cancelled) return;
        setStack((prev) =>
          prev.map((entry, i) =>
            i === prev.length - 1
              ? { ...entry, error: err instanceof Error ? err.message : String(err), loading: false }
              : entry,
          ),
        );
      },
    );

    return () => {
      cancelled = true;
    };
  }, [current?.path, current?.loading, fetchContext]);

  // Close on Escape — but only if we're the topmost popup
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (stack.length > 1) {
          setStack((prev) => prev.slice(0, -1));
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose, stack.length]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNestedReference = useCallback((path: string) => {
    setStack((prev) => {
      // Use the current top-of-stack's resolvedDir as the contextDir for the new entry
      const currentTop = prev[prev.length - 1];
      const contextDir = currentTop?.resolvedDir ?? context.currentPath ?? '';
      const nestedContext = {
        ...context,
        currentPath: contextDir,
      };
      const fullPath = resolveToFullPath(path, nestedContext);
      const resolvedDir = getDirectory(fullPath);
      return [...prev, { path, contextDir, resolvedDir, content: null, loading: true, error: null }];
    });
  }, [context]);

  const handleBack = useCallback(() => {
    if (stack.length > 1) {
      setStack((prev) => prev.slice(0, -1));
    } else {
      onClose();
    }
  }, [stack.length, onClose]);

  const displayName = current?.path.split('/').pop() ?? current?.path ?? '';

  return (
    <div
      className="doc-ref-popup-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Document reference: ${displayName}`}
    >
      <div
        className="doc-ref-popup-container"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        {/* Header */}
        <div className="doc-ref-popup-header">
          <div className="doc-ref-popup-title-row">
            {stack.length > 1 && (
              <button
                type="button"
                className="doc-ref-popup-back"
                onClick={handleBack}
                aria-label="Go back"
              >
                ←
              </button>
            )}
            <span className="doc-ref-popup-icon">📄</span>
            <h2 className="doc-ref-popup-title" title={current?.path}>
              {displayName}
            </h2>
            {stack.length > 1 && (
              <span className="doc-ref-popup-depth">
                {stack.length - 1} deep
              </span>
            )}
          </div>
          <button
            type="button"
            className="doc-ref-popup-close"
            onClick={onClose}
            aria-label="Close document reference"
          >
            ✕
          </button>
        </div>

        {/* Path breadcrumb */}
        <div className="doc-ref-popup-path">
          {current?.path}
        </div>

        {/* Body */}
        <div className="doc-ref-popup-body">
          {current?.loading && (
            <div className="doc-ref-popup-loading">Loading file…</div>
          )}
          {current?.error && (
            <div className="doc-ref-popup-error">
              <span className="doc-ref-popup-error-icon">⚠️</span>
              <span>{current.error}</span>
            </div>
          )}
          {!current?.loading && !current?.error && current?.content != null && (
            <MarkdownRenderer
              content={current.content}
              onDocReference={handleNestedReference}
            />
          )}
        </div>
      </div>
    </div>
  );
}
